/**
 * TalentGrid — the talent map.
 *
 * One self-contained view of everyone in scope on the Eduvos 9-box grid:
 * filters, stat tiles, the zone legend, the grid itself (performance across,
 * potential down, block numbers and action labels from the playbook), and a
 * strip for anyone who could not be placed this cycle. Each person is an
 * initials chip with a hover card.
 *
 * Scope is decided server-side from the props: `sessionId` reads that
 * session's placements and in-session decisions; without it the published
 * positions for the year are shown, org-wide for HR and two levels down for
 * a People Leader. `managerId` narrows a published view to one leader.
 *
 * Filters go to the server (facets are always computed over the unfiltered
 * scope); the search box narrows the visible chips client-side. A refetch
 * keeps the last result on screen at reduced opacity.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { AlertCircle, Clock, Search, Slash, X } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useTalentGrid } from '@/hooks/useTalentReview'
import type { TalentGridPerson, TalentGridSummary } from '@/services/api/talentReviewService'
import TalentGridPersonCard from './TalentGridPersonCard'
import IDPViewerDialog from '../IDPViewerDialog'
import BlockGuidance, { ReflectionQuestionsButton } from './BlockGuidance'
import TalentGridExport from './TalentGridExport'
import {
  CHIP_TONE_CLASS,
  NOT_PLACEABLE_CHIP_CLASS,
  AWAITING_CHIP_CLASS,
  NOT_PLACEABLE_STRIP_CLASS,
  PERFORMANCE_SUBLABEL,
  POTENTIAL_BAND,
  POTENTIAL_DEFINITION,
  POTENTIAL_HELP,
  POTENTIAL_SHORT,
  ZONE_BLOCKS,
  ZONE_CELL_CLASS,
  ZONE_LABEL,
  ZONE_TEXT_CLASS,
  ZONE_TONE,
  eduvosBlock,
  zoneOf,
  type Level,
  type Zone,
} from './eduvosBlocks'

export interface TalentGridProps {
  reviewYear: number
  /** Read this session's placements and decisions instead of the published positions. */
  sessionId?: string
  /** Narrow a published view to one People Leader (HR passes nothing for org-wide). */
  managerId?: string
  /** Show the Move controls on each person's card. */
  canMove?: boolean
  onMove?: (person: TalentGridPerson, performance: Level, potential: Level) => Promise<void> | void
  /** Record an action or note for a person from their card. */
  onAction?: (person: TalentGridPerson) => void
  showFilters?: boolean
  title?: string
  className?: string
  /** Blocks the room is on right now; the others dim. Null or empty: no emphasis. */
  highlightBlocks?: number[] | null
  /** Re-read every 20 s while the session is in the room. */
  live?: boolean
  /** The viewer, so their own team's plans open to be worked on and everyone
      else's open to be read (Victor, 9 Sep 2026). */
  currentUserId?: string
}

/** Columns left to right, rows top to bottom. */
const PERFORMANCE_COLUMNS: Level[] = ['low', 'medium', 'high']
const POTENTIAL_ROWS: Level[] = ['high', 'medium', 'low']
const ZONES: Zone[] = ['A', 'B', 'C']

const PERFORMANCE_COLUMN_LABEL: Record<Level, string> = {
  low: 'Not yet full',
  medium: 'Full',
  high: 'Exceptional',
}

/** Chips shown in a cell before it collapses behind a "+n" chip. */
const CHIP_CAP = 36

const ALL = 'all'

interface Filters {
  function?: string
  department?: string
  campus?: string
  position_type?: string
  leader_id?: string
  one_up_id?: string
}

const EMPTY_FILTERS: Filters = {}

const AXIS_STRIP = 'bg-gray-50 text-gray-600'

const byName = (a: TalentGridPerson, b: TalentGridPerson) => a.name.localeCompare(b.name)

function pctOf(value: number, base: number): string {
  if (base <= 0) return 'none placed yet'
  return `${Math.round((value / base) * 100)}% of placed`
}

function FacetSelect({
  label,
  allLabel,
  value,
  options,
  onChange,
}: {
  label: string
  allLabel: string
  value: string | undefined
  options: { value: string; label: string }[]
  onChange: (value: string | undefined) => void
}) {
  const usable = options.filter((o) => !!o.value)
  if (usable.length === 0) return null
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
      <SelectTrigger className="h-9 w-full bg-white text-xs sm:w-[170px]" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {usable.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** The summary of a list of people — what the server computes, for the
 *  client-side search so the tiles and the grid never disagree. */
function summaryOf(people: TalentGridPerson[]): TalentGridSummary {
  const placed = people.filter((p) => p.block != null)
  return {
    total: people.length,
    placed: placed.length,
    not_placeable: people.filter((p) => !!p.not_placeable_reason).length,
    pending: people.filter((p) => p.block == null && !p.not_placeable_reason).length,
    by_performance: {
      low: placed.filter((p) => p.performance === 'low').length,
      medium: placed.filter((p) => p.performance === 'medium').length,
      high: placed.filter((p) => p.performance === 'high').length,
    },
    hipo: people.filter((p) => p.is_hipo).length,
    key_persons: people.filter((p) => p.key_person).length,
  }
}

/** The counts the grid itself cannot show. The rating spread sits in the
 *  column headers and each block carries its share, so those tiles went
 *  (a campus GM, HR and the HR process owner, 8 Sep 2026: "just the percentage in the blocks"). */
export default function TalentGrid({
  reviewYear,
  sessionId,
  managerId,
  canMove = false,
  onMove,
  onAction,
  showFilters = true,
  title,
  className,
  highlightBlocks = null,
  live = false,
  currentUserId,
}: TalentGridProps) {
  // The plan a card opens. Someone else's People Leader reads it; their own
  // works on it — the same rule the high-potential list uses.
  const [planFor, setPlanFor] = useState<{ id: string; name: string; role?: string; readOnly: boolean } | null>(null)
  const openPlan = (p: TalentGridPerson) =>
    setPlanFor({
      id: p.employee_id,
      name: p.name,
      role: p.job_title || undefined,
      readOnly: !currentUserId || p.leader_id !== currentUserId,
    })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [expandedCells, setExpandedCells] = useState<Set<number>>(() => new Set())
  // The unfiltered scope size, remembered from the last unfiltered response
  // so "Showing N of M" keeps its M while filters are on.
  const [scopedTotal, setScopedTotal] = useState<number | null>(null)

  // A new scope starts clean — reset during render so the first request for
  // the new scope never carries the old filters.
  const scopeKey = `${reviewYear}|${sessionId ?? ''}|${managerId ?? ''}`
  const [seenScope, setSeenScope] = useState(scopeKey)
  const scopeChanged = seenScope !== scopeKey
  if (scopeChanged) {
    setSeenScope(scopeKey)
    setSearch('')
    setFilters(EMPTY_FILTERS)
    setExpandedCells(new Set())
    setScopedTotal(null)
  }
  const activeFilters = scopeChanged ? EMPTY_FILTERS : filters

  const { data, isLoading, isFetching, isPlaceholderData, error } = useTalentGrid(
    {
      reviewYear,
      sessionId,
      managerId,
      ...activeFilters,
    },
    true,
    { refetchInterval: live ? 20000 : false },
  )
  const focus = highlightBlocks && highlightBlocks.length > 0 ? new Set(highlightBlocks) : null

  const hasServerFilters = Object.values(activeFilters).some(Boolean)
  const hasAnyFilter = hasServerFilters || search.trim().length > 0

  useEffect(() => {
    if (data && !hasServerFilters && !isPlaceholderData) setScopedTotal(data.summary.total)
  }, [data, hasServerFilters, isPlaceholderData])

  const people = useMemo(() => data?.people ?? [], [data])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return people
    return people.filter((p) => `${p.name} ${p.job_title || ''}`.toLowerCase().includes(q))
  }, [people, search])

  const { cells, notPlaceable, awaiting } = useMemo(() => {
    const cellMap = new Map<number, TalentGridPerson[]>()
    const np: TalentGridPerson[] = []
    const waiting: TalentGridPerson[] = []
    for (const p of visible) {
      if (p.block && p.block >= 1 && p.block <= 9) {
        const list = cellMap.get(p.block) || []
        list.push(p)
        cellMap.set(p.block, list)
      } else if (p.not_placeable_reason) {
        np.push(p)
      } else {
        waiting.push(p)
      }
    }
    for (const list of cellMap.values()) list.sort(byName)
    np.sort(byName)
    waiting.sort(byName)
    return { cells: cellMap, notPlaceable: np, awaiting: waiting }
  }, [visible])

  const setFilter = (key: keyof Filters) => (value: string | undefined) =>
    setFilters((prev) => ({ ...prev, [key]: value }))

  const clearFilters = () => {
    setSearch('')
    setFilters(EMPTY_FILTERS)
  }

  const expandCell = (block: number) =>
    setExpandedCells((prev) => {
      const next = new Set(prev)
      next.add(block)
      return next
    })

  const heading = title || (data?.mode === 'session' ? 'Placements on the grid' : 'Talent map')
  const subtitle = data
    ? data.mode === 'session'
      ? `${data.session?.session_name || 'Annual I&G Review'} · ${data.review_year}`
      : `Published placements for ${data.review_year}`
    : `${reviewYear}`

  // --- Loading / error ------------------------------------------------------
  if (isLoading && !data) {
    return (
      <Card className={className} data-demo="talent-grid">
        <CardContent className="py-8 text-sm text-muted-foreground">Loading the talent map…</CardContent>
      </Card>
    )
  }
  if (!data) {
    return (
      <Card className={cn('border-red-200', className)} data-demo="talent-grid">
        <CardContent className="flex items-center gap-2 py-6 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {(error as Error | null)?.message || 'Could not load the talent map.'}
        </CardContent>
      </Card>
    )
  }

  const summary = search.trim() ? summaryOf(visible) : data.summary
  const share = (n: number) => (summary.placed > 0 ? `${Math.round((n / summary.placed) * 100)}%` : null)
  // Down the side as well as across the top: a row is a potential band, and the
  // spread down it is as much of a talking point as the spread across
  // (HR process owner, 9 Sep 2026). Counted off the same people the cells draw.
  const inRow = (pot: Level) => visible.filter((p) => p.block != null && p.potential === pot).length
  const moved = visible.filter((p) => p.position_changed).length
  const counts = [
    `${summary.placed} of ${summary.total} placed`,
    summary.pending > 0 ? `${summary.pending} awaiting placement` : null,
    summary.hipo > 0 ? `${summary.hipo} high-potential` : null,
    summary.key_persons > 0 ? `${summary.key_persons} key ${summary.key_persons === 1 ? 'person' : 'persons'}` : null,
    summary.not_placeable > 0 ? `${summary.not_placeable} shown, not placed` : null,
    moved > 0 ? `${moved} moved in the room` : null,
  ].filter(Boolean)
  const showingOf = scopedTotal ?? data.summary.total
  const refreshing = isFetching && !isLoading

  return (
    <section
      data-demo="talent-grid"
      aria-busy={refreshing || undefined}
      className={cn('space-y-4 transition-opacity duration-200', refreshing && 'opacity-60', className)}
    >
      {/* Heading */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-primary text-lg font-semibold text-heritage-blue">{heading}</h3>
            <ReflectionQuestionsButton />
          </div>
          <p className="text-sm text-gray-500">{subtitle}</p>
          <p className="text-sm text-gray-600 tabular-nums" data-demo="talent-grid-counts">
            {counts.join(' · ')}
          </p>
          {data.session?.seats_hidden && (
            <p className="text-xs text-muted-foreground" data-demo="talent-grid-seats-hidden">
              You are placed in this session yourself, so how its People Leaders are placed is not shown to you.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {refreshing && <span className="text-xs text-gray-400">Updating…</span>}
          {/* On paper with full names, or in Excel with the detail (a campus GM and HR — 8 Sep 2026) */}
          <TalentGridExport
            people={visible}
            title={heading}
            subtitle={subtitle}
            reviewYear={data.review_year}
            pdfQuery={{ sessionId, managerId, ...activeFilters }}
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {(error as Error).message || 'The latest refresh failed; showing the last result.'}
        </div>
      )}

      {/* 1. Filters */}
      {showFilters && (
        <div
          data-demo="talent-grid-filters"
          className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 shadow-sm"
        >
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or title"
              aria-label="Search people"
              className="h-9 bg-white pl-8 text-sm"
            />
          </div>
          <FacetSelect
            label="Function"
            allLabel="All functions"
            value={filters.function}
            options={data.facets.functions.map((v) => ({ value: v, label: v }))}
            onChange={setFilter('function')}
          />
          <FacetSelect
            label="Department"
            allLabel="All departments"
            value={filters.department}
            options={data.facets.departments.map((v) => ({ value: v, label: v }))}
            onChange={setFilter('department')}
          />
          <FacetSelect
            label="Campus"
            allLabel="All campuses"
            value={filters.campus}
            options={data.facets.campuses.map((v) => ({ value: v, label: v }))}
            onChange={setFilter('campus')}
          />
          <FacetSelect
            label="Position type"
            allLabel="All position types"
            value={filters.position_type}
            options={data.facets.position_types.map((v) => ({ value: v, label: v }))}
            onChange={setFilter('position_type')}
          />
          <FacetSelect
            label="One-Up"
            allLabel="All One-Ups"
            value={filters.one_up_id}
            options={data.facets.one_ups.map((l) => ({ value: l.id, label: l.name }))}
            onChange={setFilter('one_up_id')}
          />
          <FacetSelect
            label="People Leader"
            allLabel="All People Leaders"
            value={filters.leader_id}
            options={data.facets.leaders.map((l) => ({ value: l.id, label: l.name }))}
            onChange={setFilter('leader_id')}
          />
          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <span className="tabular-nums">
              Showing {visible.length} of {showingOf}
            </span>
            {hasAnyFilter && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 gap-1 text-xs">
                <X className="h-3.5 w-3.5" />
                Clear filters
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 2. Legend — one colour for the grid; only the high-potential band is tinted (HR process owner, 7 Sep 2026) */}
      <ul
        data-demo="talent-grid-legend"
        className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-600"
        aria-label="Legend"
      >
        <li className="inline-flex items-center gap-2">
          <span aria-hidden className={cn('h-3.5 w-3.5 rounded-full', CHIP_TONE_CLASS.green)} />
          <span>
            <span className="font-semibold text-gray-800">Blocks 1, 2 and 3</span> · high-potentials
          </span>
        </li>
        {data.summary.not_placeable > 0 && (
          <li className="inline-flex items-center gap-2">
            <span aria-hidden className={cn('flex h-4 w-4 items-center justify-center rounded-full', NOT_PLACEABLE_CHIP_CLASS)}>
              <Slash className="h-2.5 w-2.5 text-gray-600" strokeWidth={2.5} />
            </span>
            <span>Shown, not placed</span>
          </li>
        )}
        {data.mode === 'session' && data.summary.pending > 0 && (
          <li className="inline-flex items-center gap-2">
            <span aria-hidden className={cn('flex h-4 w-4 items-center justify-center rounded-full', AWAITING_CHIP_CLASS)}>
              <Clock className="h-2.5 w-2.5 text-gray-600" strokeWidth={2.5} />
            </span>
            <span>Awaiting placement</span>
          </li>
        )}
        {data.summary.key_persons > 0 && (
          <li className="inline-flex items-center gap-2">
            <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-amber-400 ring-1 ring-white" />
            <span>Key person</span>
          </li>
        )}
      </ul>

      {data.summary.total === 0 && (
        <p className="text-sm text-gray-500">
          {data.mode === 'session'
            ? 'No reviewable people were found for this session.'
            : `No published placements for ${data.review_year} yet.`}
        </p>
      )}

      {/* 4. The grid */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <div
          className="grid min-w-[680px] grid-cols-[7.5rem_repeat(3,minmax(0,1fr))]"
          role="table"
          aria-label={`Talent map for ${data.review_year}: performance across, potential down`}
        >
          {/* Axis header row */}
          <div role="row" className="contents">
            <div role="columnheader" className={cn('border-b border-r px-3 py-2', AXIS_STRIP)}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Potential ↓</p>
              <p className="text-[10px] leading-tight text-gray-500">{POTENTIAL_DEFINITION}</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Performance →</p>
            </div>
            {PERFORMANCE_COLUMNS.map((perf, i) => (
              <div
                key={perf}
                role="columnheader"
                className={cn('border-b px-3 py-2 text-center', i < PERFORMANCE_COLUMNS.length - 1 && 'border-r', AXIS_STRIP)}
              >
                <p className="text-sm font-semibold">
                  {PERFORMANCE_COLUMN_LABEL[perf]}
                  {summary.placed > 0 && (
                    <span className="ml-1.5 text-xs font-medium tabular-nums text-gray-500" data-demo={`talent-grid-col-share-${perf}`}>
                      {summary.by_performance[perf]} · {share(summary.by_performance[perf])}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-gray-500">{PERFORMANCE_SUBLABEL[perf]}</p>
              </div>
            ))}
          </div>

          {POTENTIAL_ROWS.map((pot, rowIndex) => {
            const lastRow = rowIndex === POTENTIAL_ROWS.length - 1
            return (
              <Fragment key={pot}>
                <div role="row" className="contents">
                  <div
                    role="rowheader"
                    title={POTENTIAL_HELP[pot]}
                    className={cn('flex flex-col justify-center border-r px-3 py-3', !lastRow && 'border-b', AXIS_STRIP)}
                  >
                    <p className="text-sm font-semibold">
                      {POTENTIAL_SHORT[pot]}
                      {summary.placed > 0 && (
                        <span
                          className="ml-1.5 text-xs font-medium tabular-nums text-gray-500"
                          data-demo={`talent-grid-row-share-${pot}`}
                        >
                          {inRow(pot)} · {share(inRow(pot))}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] leading-tight text-gray-500">{POTENTIAL_BAND[pot]}</p>
                  </div>
                  {PERFORMANCE_COLUMNS.map((perf, colIndex) => {
                    const block = eduvosBlock(perf, pot)
                    const zone = zoneOf(block)
                    if (!block || !zone) return null
                    const list = cells.get(block.number) || []
                    const expanded = expandedCells.has(block.number)
                    const shown = expanded ? list : list.slice(0, CHIP_CAP)
                    const hidden = list.length - shown.length
                    const lastCol = colIndex === PERFORMANCE_COLUMNS.length - 1
                    return (
                      <div
                        key={perf}
                        role="cell"
                        data-demo={`talent-grid-cell-${block.number}`}
                        data-focus={focus ? (focus.has(block.number) ? 'on' : 'off') : undefined}
                        className={cn(
                          'flex min-h-[9.5rem] flex-col gap-2 p-3 transition-opacity',
                          !lastRow && 'border-b',
                          !lastCol && 'border-r',
                          ZONE_CELL_CLASS[zone],
                          focus && !focus.has(block.number) && 'opacity-40',
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <span className="inline-flex items-center gap-1">
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                                  CHIP_TONE_CLASS[ZONE_TONE[zone]],
                                )}
                              >
                                Block {block.number}
                              </span>
                              <BlockGuidance block={block.number} />
                            </span>
                          </div>
                          <span className="whitespace-nowrap text-[11px] tabular-nums text-gray-600" data-demo={`talent-grid-cell-share-${block.number}`}>
                            {list.length === 1 ? '1 person' : `${list.length} people`}
                            {list.length > 0 && share(list.length) ? ` · ${share(list.length)}` : ''}
                          </span>
                        </div>
                        {list.length === 0 ? (
                          <p className="text-sm text-gray-400">
                            <span aria-hidden>—</span>
                            <span className="sr-only">No one in this block</span>
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {shown.map((p, i) => (
                              <motion.div
                                key={p.employee_id}
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: i * 0.01, duration: 0.18 }}
                              >
                                <TalentGridPersonCard
                                  person={p}
                                  reviewYear={data.review_year}
                                  canMove={canMove}
                                  onMove={onMove}
                                  onAction={onAction}
                                  onOpenPlan={openPlan}
                                />
                              </motion.div>
                            ))}
                            {hidden > 0 && (
                              <button
                                type="button"
                                onClick={() => expandCell(block.number)}
                                aria-label={`Show ${hidden} more ${hidden === 1 ? 'person' : 'people'} in block ${block.number}`}
                                className="inline-flex h-7 min-w-7 items-center justify-center rounded-full border border-gray-300 bg-white/90 px-1.5 text-[11px] font-semibold text-gray-700 outline outline-2 outline-white hover:bg-white focus-visible:ring-2 focus-visible:ring-heritage-blue focus-visible:ring-offset-2"
                              >
                                +{hidden}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </Fragment>
            )
          })}
        </div>
      </div>

      {/* 5. Kept off the grid by a list handed in before 22 Sep 2026 / awaiting placement */}
      {(notPlaceable.length > 0 || awaiting.length > 0) && (
        <div
          data-demo="talent-grid-not-placeable"
          className={cn('space-y-3 rounded-xl border border-dashed p-3', NOT_PLACEABLE_STRIP_CLASS)}
        >
          {notPlaceable.length > 0 && (
            <div>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  <Slash className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                  Too soon to rate — shown, not placed
                </p>
                <p className="text-[11px] text-gray-500">
                  {notPlaceable.length === 1 ? '1 person' : `${notPlaceable.length} people`} · not on the grid this year; the reason shows on each person
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {notPlaceable.map((p, i) => (
                  <motion.div
                    key={p.employee_id}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.01, duration: 0.18 }}
                  >
                    <TalentGridPersonCard person={p} reviewYear={data.review_year} canMove={canMove} onMove={onMove} onAction={onAction} onOpenPlan={openPlan} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}
          {awaiting.length > 0 && (
            <div>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  <Clock className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                  Awaiting placement
                </p>
                <p className="text-[11px] text-gray-500">
                  {awaiting.length === 1 ? '1 person' : `${awaiting.length} people`} · their People Leader has not submitted yet
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {awaiting.map((p, i) => (
                  <motion.div
                    key={p.employee_id}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.01, duration: 0.18 }}
                  >
                    <TalentGridPersonCard person={p} reviewYear={data.review_year} canMove={canMove} onMove={onMove} onAction={onAction} onOpenPlan={openPlan} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {planFor && (
        <IDPViewerDialog
          open
          onOpenChange={(o) => !o && setPlanFor(null)}
          memberName={planFor.name}
          memberId={planFor.id}
          memberRole={planFor.role}
          readOnly={planFor.readOnly}
        />
      )}
    </section>
  )
}
